import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config/apiConfig';
import { FaRobot, FaTimes } from 'react-icons/fa';
import './FloatingSupport.css';

const FloatingSupport = () => {
  const [chatLink, setChatLink] = useState('https://tawk.to/chat/69df26699191331c2d1875fe/1jm7qs3pb');
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Position and drag state
  const [position, setPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const containerRef = useRef(null);
  const dragStart = useRef({ x: 0, y: 0 });
  const elementStart = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  useEffect(() => {
    const fetchChatLink = async () => {
      try {
        const response = await axios.get(`${API_URL}/content/public/support-chat-link`);
        if (response.data.success && response.data.chatLink) {
          setChatLink(response.data.chatLink);
        }
      } catch (error) {
        console.error('Error fetching chat link:', error);
      }
    };

    fetchChatLink();
    
    // Show immediately
    setIsVisible(true);
  }, []);

  // Handle orientation change / resize clamping
  useEffect(() => {
    const handleResize = () => {
      if (position && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        setPosition(prev => {
          if (!prev) return null;
          return {
            left: Math.max(0, Math.min(prev.left, viewportWidth - rect.width)),
            top: Math.max(0, Math.min(prev.top, viewportHeight - rect.height))
          };
        });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  // Drag handlers for touch events
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    dragStart.current = { x: touch.clientX, y: touch.clientY };
    elementStart.current = { x: rect.left, y: rect.top };
    hasMoved.current = false;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 0) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - dragStart.current.x;
    const deltaY = touch.clientY - dragStart.current.y;
    
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      hasMoved.current = true;
    }
    
    if (hasMoved.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let newLeft = elementStart.current.x + deltaX;
      let newTop = elementStart.current.y + deltaY;
      
      // Clamp inside viewport
      newLeft = Math.max(0, Math.min(newLeft, viewportWidth - rect.width));
      newTop = Math.max(0, Math.min(newTop, viewportHeight - rect.height));
      
      setPosition({ left: newLeft, top: newTop });
      
      if (e.cancelable) {
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = (e) => {
    setIsDragging(false);
    if (hasMoved.current) {
      e.preventDefault();
      setTimeout(() => {
        hasMoved.current = false;
      }, 50);
    }
  };

  // Drag handlers for mouse events (testing/desktop/compatibility)
  const handleMouseDown = (e) => {
    // Only drag on left click
    if (e.button !== 0) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    dragStart.current = { x: e.clientX, y: e.clientY };
    elementStart.current = { x: rect.left, y: rect.top };
    hasMoved.current = false;
    setIsDragging(true);
    
    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - dragStart.current.x;
      const deltaY = moveEvent.clientY - dragStart.current.y;
      
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        hasMoved.current = true;
      }
      
      if (hasMoved.current) {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        let newLeft = elementStart.current.x + deltaX;
        let newTop = elementStart.current.y + deltaY;
        
        newLeft = Math.max(0, Math.min(newLeft, viewportWidth - rect.width));
        newTop = Math.max(0, Math.min(newTop, viewportHeight - rect.height));
        
        setPosition({ left: newLeft, top: newTop });
      }
    };
    
    const handleMouseUp = (upEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setIsDragging(false);
      
      if (hasMoved.current) {
        upEvent.preventDefault();
        upEvent.stopPropagation();
        setTimeout(() => {
          hasMoved.current = false;
        }, 50);
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleClick = (e) => {
    if (hasMoved.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    toggleModal();
  };

  const containerStyle = position ? {
    left: `${position.left}px`,
    top: `${position.top}px`,
    bottom: 'auto',
    right: 'auto',
    transform: 'none',
    transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.1)'
  } : {};

  return (
    <>
      <div 
        ref={containerRef}
        className={`floating-support-container ${isVisible ? 'visible' : ''} ${isModalOpen ? 'active' : ''}`}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        style={containerStyle}
        title={isModalOpen ? "Close Chat" : "Chat with Support"}
      >
        <div className="floating-support-pulse"></div>
        <div className="floating-support-button">
          {isModalOpen ? <FaTimes className="support-icon" /> : <FaRobot className="support-icon-ai" />}
          {!isModalOpen && <span className="support-tooltip">Chat with AI Support</span>}
        </div>
      </div>

      {isVisible && (
        <div className={`chat-modal-overlay ${isModalOpen ? 'show' : ''}`}>
          <div className="chat-modal-wrapper">
            <div className="chat-modal-header">
              <div className="chat-header-info">
                <div className="chat-status-dot"></div>
                <span>AI Assistant</span>
              </div>
              <button
                className="chat-close-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleModal();
                }}
              >
                <FaTimes />
              </button>
            </div>
            <div className="chat-iframe-container">
              <iframe
                src={chatLink}
                title="Support Chat"
                frameBorder="0"
                scrolling="no"
                allow="autoplay"
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingSupport;
